import { DropdownActionItem, DropdownActions } from '../../../../../../widgets/dropdown';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import CopyIcon from 'src/shared/material_icons/content/content_copy.svg';
import RemoveIcon from 'src/shared/material_icons/action/delete.svg';
import AreaIcon from 'src/shared/material_icons/maps/category.svg';
import MoveIcon from 'src/shared/material_icons/content/forward.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import styled, { css } from 'styled-components';
import { markMatches } from '../../../configurator-components/TreeComponent';
import { ComposerAreaNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerRowActionButton as ActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { getComposerAreaValidationMessages } from '../helpers/composerAttributeValidation';
import ComposerValidationTooltip from './composerValidationTooltip';

export type ComposerAreaRowAction = 'move' | 'duplicate' | 'remove';

const Row = styled.div<{ $selected: boolean; $indent: number }>`
    position: relative;
    display: flex;
    width: calc(100% - ${(props) => props.$indent}px);
    min-width: 0;
    height: 34px;
    align-items: center;
    gap: 7px;
    margin-left: ${(props) => props.$indent}px;
    padding: 0 6px;
    color: var(--composer-fg, #1e2a38);
    background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'transparent')};
    /* La riga scelta: lavato e filo arancio, come in ogni albero dei due editor (treeRowLook). */
    box-shadow: ${(props) => (props.$selected ? 'inset 0 0 0 1px var(--composer-accent, #e8650a)' : 'none')};
    border-radius: 8px;
    cursor: pointer;
    outline: none;
    user-select: none;

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'var(--composer-hover, #e9eff5)')};
    }

    > div:last-child > * {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }

    ${(props) =>
        props.$selected &&
        css`
            > div:last-child > * {
                opacity: 1;
            }
        `}
`;

const Main = styled.div`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 8px;
`;

const Icon = styled.span`
    display: inline-flex;
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    color: var(--composer-kind-attribute, #0e7c7c);

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }

    /* L'IMMAGINE DELL'AREA AL POSTO DEL GLIFO (utente, 2026-10-16), come per opzioni e gruppi. */
    img {
        width: 100%;
        height: 100%;
        border-radius: 3px;
        object-fit: cover;
    }
`;

const Name = styled.span`
    overflow: hidden;
    font-size: 13px;
    /* Peso normale per ogni riga, come il prototipo (utente, 2026-10-15). */
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Actions = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
`;

export const ComposerAreaTreeElement: FC<{
    area: ComposerAreaNode;
    indent: number;
    selected: boolean;
    highlight?: string;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, area: ComposerAreaNode) => void;
    onDoubleClick?: (area: ComposerAreaNode) => void;
    onAction: (action: ComposerAreaRowAction, area: ComposerAreaNode) => void;
    /** La riga intera si trascina (utente, 2026-10-15): il gesto lo conduce chi ospita le righe (`startPointerReorder`). */
    onRowPointerDown: (event: React.PointerEvent<HTMLElement>, area: ComposerAreaNode) => void;
    /** La profondità della riga nell'albero: dice al trascinamento di chi è figlia. */
    level?: number;
}> = ({ area, indent, selected, highlight, onSelect, onDoubleClick, onAction, onRowPointerDown, level = 1 }) => {
    const validationMessages = getComposerAreaValidationMessages(area);
    return (
        <Row
            $indent={indent}
            $selected={selected}
            data-composer-row={area.id}
            data-reorder-id={area.id}
            onPointerDown={(event) => onRowPointerDown(event, area)}
            data-reorder-level={level}
            aria-selected={selected}
            onClick={(event) => onSelect(event, area)}
            onDoubleClick={() => onDoubleClick?.(area)}
            role='treeitem'
            tabIndex={0}
        >
            <Main>
                <ZTooltip title={T._('Customizable area', 'Admin')}>
                    <Icon data-reorder-anchor>{area.img ? <img src={area.img} alt='' /> : <AreaIcon />}</Icon>
                </ZTooltip>
                <Name title={area.name}>{markMatches(area.name, highlight)}</Name>
                {validationMessages.length > 0 && <ComposerValidationTooltip messages={validationMessages} />}
            </Main>
            <Actions>
                <DropdownActions
                    header={
                        <ActionButton type='button' aria-label={T._('More commands', 'Admin')}>
                            <MoreIcon />
                        </ActionButton>
                    }
                    items={[
                        <DropdownActionItem key='move' icon={<MoveIcon />} onClick={() => onAction('move', area)}>
                            {T._('Move…', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem key='duplicate' icon={<CopyIcon />} onClick={() => onAction('duplicate', area)}>
                            {T._('Clone', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem key='remove' danger icon={<RemoveIcon />} onClick={() => onAction('remove', area)}>
                            {T._('Remove', 'Admin')}
                        </DropdownActionItem>
                    ]}
                    minWidth='250px'
                    orientation='RIGHT'
                />
            </Actions>
        </Row>
    );
};
